import { useEffect, useMemo, useState } from 'react'
import { AdminAlert } from '../AdminShared'
import { adminGetPermissionCatalog, adminSetPermissions } from '../../../services/admin'
import { ADMIN_PERMISSIONS, PERMISSION_LABELS } from '../../../utils/adminPermissions'

// Super-admin editor for one admin's permission list.
// PATCH /admin/:id/permissions REPLACES the list, so we always send the full set.
export function PermissionsModal({ admin, onClose, onSaved }) {
  const [catalog, setCatalog] = useState(ADMIN_PERMISSIONS)
  const [catalogLoading, setCatalogLoading] = useState(true)
  const [selected, setSelected] = useState(() => new Set(admin.adminPermissions ?? []))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let alive = true
    adminGetPermissionCatalog()
      .then(list => {
        if (!alive || list.length === 0) return
        setCatalog(list.map(p => ({
          key: p.key,
          label: PERMISSION_LABELS[p.key] ?? p.key,
          description: p.description ?? '',
        })))
      })
      .catch(() => {}) // keep the built-in list if the catalog call fails
      .finally(() => alive && setCatalogLoading(false))
    return () => { alive = false }
  }, [])

  const original = useMemo(() => [...(admin.adminPermissions ?? [])].sort().join('|'), [admin])
  const dirty = [...selected].sort().join('|') !== original

  const toggle = (key) => setSelected(prev => {
    const next = new Set(prev)
    next.has(key) ? next.delete(key) : next.add(key)
    return next
  })

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await adminSetPermissions(admin.id, [...selected])
      onSaved(admin.id, Array.isArray(res?.adminPermissions) ? res.adminPermissions : [...selected])
    } catch (err) {
      setError(err.message ?? 'Failed to update permissions')
    } finally {
      setSaving(false)
    }
  }

  const name = admin.profile?.fullName || admin.email

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-on-background/60 p-0 sm:p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        className="bg-surface border-4 border-on-background w-full sm:max-w-lg max-h-[92vh] flex flex-col"
        style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}
      >
        <div className="flex items-start justify-between gap-3 p-4 sm:p-6 border-b-4 border-on-background">
          <div className="min-w-0">
            <h2 className="font-extrabold text-[14px] uppercase tracking-widest">Permissions</h2>
            <p className="text-[12px] text-on-surface-variant truncate">{name}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="p-1 hover:bg-surface-container transition-colors shrink-0">
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 flex flex-col gap-4">
          {error && <AdminAlert type="error" message={error} />}

          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">
              {selected.size} of {catalog.length} granted{catalogLoading ? ' · loading…' : ''}
            </p>
            <div className="flex gap-2">
              <button type="button" onClick={() => setSelected(new Set(catalog.map(p => p.key)))}
                className="px-2.5 py-1 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-surface-container transition-colors">
                All
              </button>
              <button type="button" onClick={() => setSelected(new Set())}
                className="px-2.5 py-1 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-surface-container transition-colors">
                None
              </button>
            </div>
          </div>

          <ul className="flex flex-col gap-2">
            {catalog.map(p => {
              const on = selected.has(p.key)
              return (
                <li key={p.key}>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={on}
                    onClick={() => toggle(p.key)}
                    className={`w-full flex items-start gap-3 text-left p-3 border-2 transition-colors ${on ? 'border-on-background bg-primary-container/20' : 'border-on-background/20 hover:bg-surface-container'}`}
                  >
                    <span className={`mt-0.5 w-10 h-5 shrink-0 border-2 border-on-background relative ${on ? 'bg-primary-container' : 'bg-surface-container'}`}>
                      <span className={`absolute top-0.5 w-3.5 h-3.5 bg-on-background transition-all ${on ? 'left-[18px]' : 'left-0.5'}`} />
                    </span>
                    <span className="min-w-0">
                      <span className="block font-bold text-[12px] uppercase tracking-widest">{p.label}</span>
                      {p.description && <span className="block text-[12px] text-on-surface-variant leading-snug mt-0.5">{p.description}</span>}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
          <p className="text-[11px] text-on-surface-variant">
            Changes apply immediately — no re-login needed. CPT distribution is a separate toggle on the admin card.
          </p>
        </div>

        <div className="flex gap-3 p-4 sm:p-6 border-t-4 border-on-background">
          <button onClick={onClose} disabled={saving}
            className="flex-1 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button onClick={save} disabled={saving || !dirty}
            className="flex-1 py-2.5 bg-on-background text-surface font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50">
            {saving ? 'Saving…' : 'Save permissions'}
          </button>
        </div>
      </div>
    </div>
  )
}
